import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Get Supabase config from environment variables
function getSupabaseConfig() {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || import.meta.env.EXPO_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

  // Validate URL format
  if (supabaseUrl && (supabaseUrl === 'your_supabase_url_here' || !supabaseUrl.startsWith('http'))) {
    console.error(
      'Invalid Supabase URL. Please set VITE_SUPABASE_URL in your environment variables.\n' +
      `Current value: ${supabaseUrl || 'undefined'}\n` +
      'Example: https://your-project.supabase.co'
    );
    return null;
  }

  if (supabaseAnonKey && supabaseAnonKey === 'your_supabase_anon_key_here') {
    console.error(
      'Invalid Supabase Anon Key. Please set VITE_SUPABASE_ANON_KEY in your environment variables.\n' +
      'Get your keys from: https://app.supabase.com/project/_/settings/api'
    );
    return null;
  }

  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn(
      'Missing Supabase environment variables.\n' +
      'Please set the following environment variables in Netlify:\n' +
      'VITE_SUPABASE_URL=your_supabase_url\n' +
      'VITE_SUPABASE_ANON_KEY=your_supabase_anon_key\n\n' +
      'Get your keys from: https://app.supabase.com/project/_/settings/api\n' +
      'Site will work but database features will be unavailable.'
    );
    return null;
  }

  return { supabaseUrl, supabaseAnonKey };
}

let supabaseClient: SupabaseClient | null = null;

/**
 * Get Supabase client instance (lazy initialization)
 * Returns null if environment variables are not configured
 */
export function getSupabaseClient(): SupabaseClient | null {
  if (supabaseClient) {
    return supabaseClient;
  }

  const config = getSupabaseConfig();
  if (!config) {
    return null;
  }

  supabaseClient = createClient(config.supabaseUrl, config.supabaseAnonKey);
  return supabaseClient;
}

/**
 * Check if Supabase is configured
 */
export function isSupabaseConfigured(): boolean {
  return getSupabaseClient() !== null;
}

/**
 * Export supabase client for backward compatibility
 * Will be null if not configured - components should check before using
 */
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    const client = getSupabaseClient();
    if (!client) {
      throw new Error(
        'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY environment variables in Netlify.'
      );
    }
    return (client as any)[prop];
  },
});

